import { resourceEduCode } from '~/api/oneBoxV3/oneBoxV3.type';
import { Authority } from '~/types/enum';

export type UserRole = Authority | '';

/** 學制名稱：國小／國中／高中；已登入但無學制為 '未驗證'；未登入為 '未登入' */
export type EduCode = resourceEduCode | '未驗證' | '未登入';

export enum TrackingEventName {
  PageView = 'page_view',
  ResourceFilter = 'resource_filter',
  ResourceCategory = 'resource_category',
  ResourceInternetTab = 'resource_internet_tab',
  ResourceInternetLinkClick = 'resource_internet_link_click',
  DownloadStart = 'download_start',
  DownloadAllStart = 'download_all_start',
  DownloadStatus = 'download_status',
  DownloadComplete = 'download_complete',
  DownloadHistory = 'download_history',
  LocalFile = 'local_file',
  AppNav = 'app_nav',
  LatestNewsReadMore = 'latest_news_read_more',
  BatchZipStarted = 'batch_zip_started',
  BatchZipTerminal = 'batch_zip_terminal',
  BatchZipFileFailed = 'batch_zip_file_failed',
  BatchZipStalled = 'batch_zip_stalled',
}

export type ResourceTaxonomyDimension = {
  edu?: string;
  subject?: string;
  year?: string;
  grade?: string;
};

export type ResourceIdentityDimension = {
  resources_url: string;
  resources_name?: string;
};

export type PageViewPayload = {
  page_title: string;
  page_location: string;
  from_path?: string;
};

export type ResourceFilterPayload = ResourceTaxonomyDimension;

export type ResourceCategoryPayload = ResourceTaxonomyDimension & {
  category: string;
};

export type ResourceInternetTabPayload = ResourceTaxonomyDimension & {
  tab_name: string;
};

export type ResourceInternetLinkClickPayload = ResourceTaxonomyDimension & {
  tab_name: string;
  link_name: string;
  link: string;
};

export type DownloadStartPayload = ResourceIdentityDimension & {
  event_type: string;
  edu?: string;
  subject?: string;
  category?: string;
  resources_label?: string;
  resources_sub_label?: string;
  /** 批次下載逐檔發送時的序號（0..N-1）；確保同批事件唯一，避免 GA4 因參數截斷後相同而去重 */
  batch_index?: number;
};

export type DownloadAllStartPayload = {
  edu?: string;
  subject?: string;
  category?: string;
  resources_label?: string;
  resources_sub_label?: string;
};

export type DownloadStatusPayload = ResourceIdentityDimension & {
  event_type: string;
};

export type DownloadCompletePayload = ResourceIdentityDimension;

export type DownloadHistoryPayload = ResourceIdentityDimension & {
  event_type: string;
};

export type LocalFilePayload = {
  tab_name: string;
  category?: string;
};

export type AppNavPayload = {
  tab_name: string;
};

export type LatestNewsReadMorePayload = ResourceTaxonomyDimension & {
  news_title: string;
};

/**
 * 網頁版「全部下載」打包成 zip 的觀測（NBX3-954）。四個事件以 batch_id 串起來；
 * batch_id 只當事件參數，不要註冊成 GA 自訂維度（每批一個值，基數太高）。
 * 分母用 started，不用既有的 download_all_start：後者是跨平台的行為指標，改它會動到既有報表。
 */
export type BatchZipStartedPayload = {
  batch_id: string;
  file_count: number;
  total_bytes: number;
  stream_saver_mode: string;
  edu?: string;
  subject?: string;
  category?: string;
};

/**
 * 不叫 download_complete：串流關閉不代表瀏覽器已經把檔案寫進硬碟。
 * partial 是串流正常關閉、但有檔案是 HTTP 錯誤頁被打包進去。
 */
export type BatchZipTerminalPayload = {
  batch_id: string;
  outcome: 'success' | 'failure' | 'partial';
  stage?: string;
  error_kind?: string;
  files_started: number;
  files_completed: number;
  http_failures: number;
  bytes_accepted: number;
  duration_ms: number;
  visibility_state?: string;
};

/** 單一檔案沒交給打包程式。現行行為下這之後整批會卡住，不會再有 terminal。 */
export type BatchZipFileFailedPayload = {
  batch_id: string;
  file_index: number;
  reason: string;
  files_completed: number;
};

export type BatchZipStalledPayload = {
  batch_id: string;
  stage: string;
  current_file_index: number;
  bytes_accepted: number;
  ms_since_last_progress: number;
  visibility_state?: string;
};

export type GaEventPayload = Record<
  string,
  string | number | boolean | undefined
>;
